import { useCallback, useEffect } from 'react';
import SessionConstants from '../../config/SessionConstants.js';
import APIUtils from '../../utils/APIUtils';

const useProgramSettingsData = ({
  CUTOFF_AUTO_OPTION,
  CUTOFF_MANUAL_OPTION,
  setProgramId,
  setLoading,
  setDataReady,
  setPageLoadError,
  setConditionNameByAcronym,
  setAnalyteDisplayByCode,
  setAnalyteDisplayByShortName,
  setAnalyteLookupOptions,
  setBiochemicalInstrumentation,
  setSelectedBiochemicalValues,
  setInitialBiochemicalValues,
  setCutoffSettingsSelection,
  setInitialCutoffSettingsSelection,
  setCutoffValues,
  setInitialCutoffValues,
  setDefaultCutoffValues,
  setCutoffValuesLoaded,
  setCutoffLoading,
  setExpandedCutoffRows,
  setBiochemicalError,
  setDisabledConditions,
  setEnabledConditions,
  setInitialDisabledConditions,
  setInitialEnabledConditions,
  setConditionsDirty,
  setConditionsError,
  setConditionsLoading,
  setInputValues,
  setProgramShortName,
  setReviewThreshold,
  setInitialReviewThreshold,
  setDefaultHgvsRef,
  setInitialDefaultHgvsRef,
  setInitialInterpretationCount,
  setDefaultInterpretationCount,
  setInitialInterpreters,
  setDefaultInterpreters,
  setAvailableUsers,
  setTempConditionRows,
}) => {
  const fetchRestrictedConditions = useCallback(async (progId) => {
    try {
      setConditionsLoading(true);
      const data = await APIUtils.getRestrictedConditions(progId);
      setDisabledConditions([...(data.disabledConditions || [])].sort());
      setEnabledConditions([...(data.enabledConditions || [])].sort());
      setInitialDisabledConditions([...(data.disabledConditions || [])].sort());
      setInitialEnabledConditions([...(data.enabledConditions || [])].sort());
      setConditionsDirty(false);
      setConditionsError('');
    } catch (err) {
      console.error('Error fetching restricted conditions:', err);
      setConditionsError('Failed to load condition settings.');
    } finally {
      setConditionsLoading(false);
    }
  }, [
    setConditionsLoading,
    setDisabledConditions,
    setEnabledConditions,
    setInitialDisabledConditions,
    setInitialEnabledConditions,
    setConditionsDirty,
    setConditionsError,
  ]);

  const loadCutoffValues = useCallback(async (progId, setAsInitial = false) => {
    try {
      setCutoffLoading(true);
      setCutoffValuesLoaded(false);
      const cutoffResponse = await APIUtils.getCutoffValues(progId);

      // New response structure: { defaultCutOff: [...], manualCutOff: [...] }
      const defaultCutoffs = Array.isArray(cutoffResponse?.defaultCutOff) ? cutoffResponse.defaultCutOff : [];
      const manualCutoffs = Array.isArray(cutoffResponse?.manualCutOff) ? cutoffResponse.manualCutOff : [];

      setDefaultCutoffValues(defaultCutoffs);
      setCutoffValues(manualCutoffs);
      if (setAsInitial) {
        setInitialCutoffValues(JSON.parse(JSON.stringify(manualCutoffs)));
      }
      setTempConditionRows([]);
      setCutoffValuesLoaded(true);
      setExpandedCutoffRows({});
    } catch (err) {
      console.error('Error fetching cutoff values:', err);
      setBiochemicalError('Failed to load analyte cutoff settings.');
    } finally {
      setCutoffLoading(false);
    }
  }, [
    setCutoffLoading,
    setCutoffValuesLoaded,
    setDefaultCutoffValues,
    setCutoffValues,
    setInitialCutoffValues,
    setTempConditionRows,
    setExpandedCutoffRows,
    setBiochemicalError,
  ]);

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      setDataReady(false);
      setPageLoadError('');

      const sessionProgramId = sessionStorage.getItem(SessionConstants.PROGRAM_ID);
      setProgramId(sessionProgramId || '');

      try {
        const conditionLookupResponse = await APIUtils.getConditionsLookup();
        const conditionLookupList = Array.isArray(conditionLookupResponse) ? conditionLookupResponse : [];
        const conditionLookupMap = conditionLookupList.reduce((acc, condition) => {
          if (condition?.acronym && condition?.name) {
            acc[condition.acronym] = condition.name;
          }
          return acc;
        }, {});
        setConditionNameByAcronym(conditionLookupMap);
      } catch (err) {
        console.error('Error fetching condition lookup:', err);
        setConditionNameByAcronym({});
      }

      try {
        const analyteLookupResponse = await APIUtils.getAnalytesLookup();
        const analyteLookupList = Array.isArray(analyteLookupResponse) ? analyteLookupResponse : [];
        const analyteCodeMap = {};
        const analyteShortNameMap = {};
        const analyteOptions = [];

        analyteLookupList.forEach((analyte) => {
          const displayName = analyte?.shortName || analyte?.name || analyte?.code;
          if (!displayName) {
            return;
          }

          if (analyte?.code) {
            analyteCodeMap[analyte.code] = displayName;
          }

          if (analyte?.shortName) {
            analyteShortNameMap[analyte.shortName] = displayName;
          }

          if (analyte?.code && analyte?.shortName) {
            analyteOptions.push({
              value: analyte.code,
              label: analyte.shortName,
              description: analyte.description || analyte.name || analyte.shortName,
            });
          }
        });

        analyteOptions.sort((a, b) => (a.description || a.label).localeCompare(b.description || b.label));

        setAnalyteDisplayByCode(analyteCodeMap);
        setAnalyteDisplayByShortName(analyteShortNameMap);
        setAnalyteLookupOptions(analyteOptions);
      } catch (err) {
        console.error('Error fetching analyte lookup:', err);
        setAnalyteDisplayByCode({});
        setAnalyteDisplayByShortName({});
        setAnalyteLookupOptions([]);
      }

      try {
        if (!sessionProgramId) {
          throw new Error('Program ID is missing from session storage.');
        }

        const response = await APIUtils.getProgramSettings(sessionProgramId);
        if (response) {
          setBiochemicalInstrumentation(response.biochemicalInstrumentation || []);
          const initialSelected = {};
          (response.biochemicalInstrumentation || []).forEach(row => {
            initialSelected[row.AnalyteGroup] = {
              Instrument: row.Instrument,
              ReagentKit: row.ReagentKit,
              LotNumber: row.LotNumber,
            };
          });
          setSelectedBiochemicalValues(initialSelected);
          setInitialBiochemicalValues(initialSelected);

          const hasManualCutoff = response.hasManualCutoff === true || response.hasManualCutoff === 'true';
          const cutoffSelection = hasManualCutoff ? CUTOFF_MANUAL_OPTION : CUTOFF_AUTO_OPTION;
          setCutoffSettingsSelection(cutoffSelection);
          setInitialCutoffSettingsSelection(cutoffSelection);

          await loadCutoffValues(sessionProgramId, true);
        }

        await fetchRestrictedConditions(sessionProgramId);

        const userResponse = await APIUtils.getProgramUsers(sessionProgramId);
        userResponse.forEach(user => {
          user.fullName = user.firstName + ' ' + user.lastName;
        });

        let parsedInterpreters = [];
        if (Array.isArray(response?.defaultInterpreters)) {
          parsedInterpreters = response.defaultInterpreters.flatMap(item =>
            typeof item === 'string' ? item.split(',').map(email => email.trim()) : []
          );
        }

        const initial = [...parsedInterpreters];
        const defaults = [...parsedInterpreters];

        const initialInputVals = {};
        defaults.forEach((email, idx) => {
          const user = userResponse.find(u => u.email === email);
          initialInputVals[idx] = user?.fullName || '';
        });

        setInputValues(initialInputVals);
        setProgramShortName(response?.shortName || '');
        setReviewThreshold(response?.reviewThreshold);
        setInitialReviewThreshold(response?.reviewThreshold);
        setDefaultHgvsRef(response?.defaultHgvsRef || '');
        setInitialDefaultHgvsRef(response?.defaultHgvsRef || '');
        setInitialInterpretationCount(response?.defaultInterpretationCount);
        setDefaultInterpretationCount(response?.defaultInterpretationCount);
        setInitialInterpreters(initial);
        setDefaultInterpreters(defaults);
        setAvailableUsers(userResponse);
        setDataReady(true);
      } catch (err) {
        console.error('Error loading Program Settings:', err);
        setDataReady(false);
        // Surface the failure inline and keep the user on the page; only a fully
        // unreachable API (handled globally in APIUtils) sends them to the Error page.
        setPageLoadError('Error loading Program Settings data. Please refresh and try again.');
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [
    CUTOFF_AUTO_OPTION,
    CUTOFF_MANUAL_OPTION,
    setProgramId,
    setLoading,
    setDataReady,
    setPageLoadError,
    setConditionNameByAcronym,
    setAnalyteDisplayByCode,
    setAnalyteDisplayByShortName,
    setAnalyteLookupOptions,
    setBiochemicalInstrumentation,
    setSelectedBiochemicalValues,
    setInitialBiochemicalValues,
    setCutoffSettingsSelection,
    setInitialCutoffSettingsSelection,
    setInputValues,
    setProgramShortName,
    setReviewThreshold,
    setInitialReviewThreshold,
    setDefaultHgvsRef,
    setInitialDefaultHgvsRef,
    setInitialInterpretationCount,
    setDefaultInterpretationCount,
    setInitialInterpreters,
    setDefaultInterpreters,
    setAvailableUsers,
    setTempConditionRows,
    fetchRestrictedConditions,
    loadCutoffValues,
  ]);

  return {
    fetchRestrictedConditions,
    loadCutoffValues,
  };
};

export default useProgramSettingsData;
